export { EQUIPMENT_KINDS } from '../../shared/equipment.ts'
import type { EquipmentKind, PantryItem } from '../../shared/types.ts'

export function daysUntil(iso: string | null): number | null {
  if (!iso) return null
  const then = new Date(`${iso}T00:00:00`)
  if (Number.isNaN(then.getTime())) return null
  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  return Math.round((then.getTime() - today.getTime()) / 86_400_000)
}

export function expiryLabel(iso: string | null): { text: string; level: 'expired' | 'urgent' | 'soon' | 'ok' } | null {
  const d = daysUntil(iso)
  if (d === null) return null
  if (d < 0) return { text: `expired ${-d}d ago`, level: 'expired' }
  if (d === 0) return { text: 'today', level: 'urgent' }
  if (d === 1) return { text: 'tomorrow', level: 'urgent' }
  if (d <= 4) return { text: `${d} days`, level: 'urgent' }
  if (d <= 10) return { text: `${d} days`, level: 'soon' }
  return { text: `${d} days`, level: 'ok' }
}

export function amountText(item: PantryItem): string {
  if (item.quantity == null) return item.unit ?? ''
  const q = Number.isInteger(item.quantity) ? item.quantity : Math.round(item.quantity * 100) / 100
  return item.unit ? `${q} ${item.unit}` : String(q)
}

export const cx = (...parts: Array<string | false | null | undefined>): string => parts.filter(Boolean).join(' ')

export function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime()
  const mins = Math.round(diff / 60_000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.round(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  const days = Math.round(hrs / 24)
  if (days < 30) return `${days}d ago`
  return new Date(iso).toLocaleDateString()
}

export function recipeToMarkdown(r: {
  title: string; blurb: string; totalMinutes: number; servings: number
  ingredients: Array<{ item: string; amount: string; note?: string }>
  steps: string[]; tips: string[]; sources: Array<{ title: string; url: string }>
}): string {
  return [
    `# ${r.title}`,
    '',
    r.blurb,
    '',
    `**Serves ${r.servings} · ${r.totalMinutes} min**`,
    '',
    '## Ingredients',
    ...r.ingredients.map((i) => `- ${[i.amount, i.item].filter(Boolean).join(' ')}${i.note ? ` _(${i.note})_` : ''}`),
    '',
    '## Method',
    ...r.steps.map((s, i) => `${i + 1}. ${s}`),
    ...(r.tips.length ? ['', '## Tips', ...r.tips.map((t) => `- ${t}`)] : []),
    ...(r.sources.length ? ['', '## Sources', ...r.sources.map((s) => `- [${s.title}](${s.url})`)] : []),
    '',
  ].join('\n')
}

/* ---------- equipment ---------- */

export const KIND_LABEL: Record<EquipmentKind, string> = {
  appliance: 'Appliances',
  cookware: 'Pots & pans',
  bakeware: 'Bakeware',
  tool: 'Tools',
}

